<template>
  <el-dialog
    v-model="dialogVisible"
    title="资料详情"
    width="920px"
    destroy-on-close
    @close="handleClose"
  >
    <div v-loading="loading" class="space-y-4">
      <template v-if="materialDetail">
        <div class="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div class="rounded-lg bg-slate-50 p-3">
            <div class="text-xs text-slate-500">资料名称</div>
            <div class="mt-1 text-sm font-medium text-slate-900">{{ materialDetail.name || '-' }}</div>
          </div>
          <div class="rounded-lg bg-slate-50 p-3">
            <div class="text-xs text-slate-500">资料类型</div>
            <div class="mt-1 text-sm font-medium text-slate-900">{{ materialDetail.definitionName || materialDetail.definitionCode || '-' }}</div>
          </div>
          <div class="rounded-lg bg-slate-50 p-3">
            <div class="text-xs text-slate-500">客户可见性</div>
            <div class="mt-1 text-sm font-medium text-slate-900">{{ materialDetail.visibleToCustomer ? '对客户可见' : '对客户隐藏' }}</div>
          </div>
          <div class="rounded-lg bg-slate-50 p-3">
            <div class="text-xs text-slate-500">最后更新时间</div>
            <div class="mt-1 text-sm font-medium text-slate-900">{{ $formatDateTime(materialDetail.updateTime || materialDetail.lastUploadTime || materialDetail.createTime) }}</div>
          </div>
        </div>

        <div v-if="materialDetail.expireDate" class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          有效期截止：{{ $formatDateTime(materialDetail.expireDate) }}
        </div>

        <div>
          <div class="mb-2 text-sm font-medium text-slate-700">表单内容</div>
          <div v-if="formEntries.length" class="grid gap-3 md:grid-cols-2">
            <div
              v-for="entry in formEntries"
              :key="entry.key"
              class="rounded-lg border border-slate-200 p-3"
            >
              <div class="text-xs text-slate-500">{{ entry.key }}</div>
              <div class="mt-1 whitespace-pre-wrap break-all text-sm text-slate-900">{{ entry.value }}</div>
            </div>
          </div>
          <div v-else class="rounded-lg border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
            暂无表单内容
          </div>
        </div>

        <div>
          <div class="mb-2 text-sm font-medium text-slate-700">文件列表</div>
          <div v-if="materialFiles.length" class="space-y-3">
            <div
              v-for="file in materialFiles"
              :key="file.id"
              class="material-file-row"
            >
              <div class="material-file-main">
                <div class="material-file-name" :title="file.originalName">{{ file.originalName || '附件' }}</div>
                <div class="material-file-meta">
                  <span>字段：{{ file.fieldName || file.fieldCode || '-' }}</span>
                  <span>版本：{{ file.version || '-' }}</span>
                  <span>上传时间：{{ $formatDateTime(file.uploadTime) }}</span>
                </div>
              </div>
              <div class="material-file-actions">
                <FileAttachment
                  variant="button"
                  :files="toPreviewFile(file)"
                  :loader="loadPreviewFile"
                />
                <button class="table-action-button is-primary" @click="handleDownloadMaterialFile(file)">下载</button>
              </div>
            </div>
          </div>
          <div v-else class="rounded-lg border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
            暂无文件
          </div>
        </div>
      </template>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <button class="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200" @click="handleClose">关闭</button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import axios from 'axios'
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { getCustomerMaterialDetailById } from '@/api/customer'
import { useUserStore } from '@/stores/user'
import FileAttachment from './FileAttachment.vue'
import { normalizeCustomerMaterialFile, withBlobPreview } from '@/utils/filePreview'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  userMaterialId: {
    type: [Number, String],
    default: null
  }
})

const emit = defineEmits(['update:visible'])

const loading = ref(false)
const materialDetail = ref(null)

const dialogVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
})

const materialFiles = computed(() => materialDetail.value?.files || [])

const formEntries = computed(() => {
  const parsed = parseJsonSafely(materialDetail.value?.formData, {})
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return []
  }
  return Object.entries(parsed).map(([key, value]) => ({
    key,
    value: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '-')
  }))
})

watch(() => [props.visible, props.userMaterialId], async ([visible, userMaterialId]) => {
  if (!visible || !userMaterialId) {
    return
  }
  loading.value = true
  try {
    materialDetail.value = await getCustomerMaterialDetailById(userMaterialId)
  } catch (error) {
    ElMessage.error(error?.message || '获取资料详情失败')
  } finally {
    loading.value = false
  }
}, { immediate: true })

const parseJsonSafely = (value, fallback) => {
  if (value === null || value === undefined || value === '') {
    return fallback
  }
  if (typeof value === 'object') {
    return value
  }
  try {
    return JSON.parse(value)
  } catch (error) {
    return fallback
  }
}

const getMaterialDownloadUrl = (file) => {
  return toPreviewFile(file).downloadSource
}

const toPreviewFile = (file) => withBlobPreview(normalizeCustomerMaterialFile(file), 'customer-material')

const getAuthHeaders = () => {
  const userStore = useUserStore()
  const headers = {}
  if (userStore.accessToken) {
    headers.Authorization = `${userStore.tokenType} ${userStore.accessToken}`
  }
  let deviceId = localStorage.getItem('deviceId')
  if (!deviceId) {
    deviceId = `WEB-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    localStorage.setItem('deviceId', deviceId)
  }
  headers['X-Device-Id'] = deviceId
  headers['X-Client-Type'] = 'web'
  return headers
}

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename || 'download'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const loadPreviewFile = async (file, options = {}) => {
  const source = options.purpose === 'download'
    ? file.downloadSource || file.downloadUrl
    : file.previewSource || file.url
  const response = await axios.get(source, {
    responseType: 'blob',
    headers: getAuthHeaders()
  })
  return {
    data: response.data,
    contentType: response?.headers?.['content-type'] || response.data?.type || file.contentType
  }
}

const handleDownloadMaterialFile = async (file) => {
  try {
    const response = await axios.get(getMaterialDownloadUrl(file), {
      responseType: 'blob',
      headers: getAuthHeaders()
    })
    downloadBlob(response.data, file?.originalName)
  } catch (error) {
    ElMessage.error(error?.message || '下载失败，请稍后重试')
  }
}

const handleClose = () => {
  emit('update:visible', false)
}
</script>

<style scoped>
.material-file-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

.material-file-main {
  min-width: 0;
  flex: 1;
}

.material-file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
}

.material-file-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
}

.material-file-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

@media (max-width: 768px) {
  .material-file-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .material-file-actions {
    width: 100%;
    flex-wrap: wrap;
  }
}
</style>
